import React, { useState, useEffect, useCallback } from "react";
import { Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { getErrorMessage } from "../../utils/errorHandler";
import InputMask from "react-input-mask";
import { DealerProps } from "../../../../store/modules/DealerCare/types.store";
import { useAppDispatch } from "store/hooks";
import { handleUpdateDealerCareDetailsRequest } from "store/modules/DealerCare/thunk.store";
import { removeCaracterPhone } from "utils/formatByText";
import * as Styled from "./styles";

export interface DealerWhatsAppConfigProps {
  dealerData: DealerProps | null;
}

interface WhatsAppFormData {
  whatsappNumber: string;
}

const DealerWhatsAppConfig: React.FC<DealerWhatsAppConfigProps> = ({
  dealerData,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);

  const { control, handleSubmit, reset } = useForm<WhatsAppFormData>({
    defaultValues: {
      whatsappNumber:
        dealerData?.whatsAppNumber?.replace(/^(\+?55)+/, "") || "",
    },
  });

  const setWhatsappNumber = useCallback(() => {
    if (dealerData?.whatsAppNumber) {
      reset({
        whatsappNumber: dealerData.whatsAppNumber.replace(/^(\+?55)+/, ""),
      });
    }
  }, [dealerData?.whatsAppNumber, reset]);

  useEffect(() => {
    setWhatsappNumber();
  }, [setWhatsappNumber]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset({
      whatsappNumber:
        dealerData?.whatsAppNumber?.replace(/^(\+?55)+/, "") || "",
    });
  };

  const onSubmit = async (data: WhatsAppFormData) => {
    if (!dealerData) {
      toast("Dados da revenda não encontrados", { type: "error" });
      return;
    }

    const removeCaracterPhones = removeCaracterPhone(data.whatsappNumber);

    let whatsAppNumberFormatted = "";
    if (removeCaracterPhones.length) {
      if (
        removeCaracterPhones.length <= 11 &&
        removeCaracterPhones.length >= 10
      ) {
        whatsAppNumberFormatted = "55".concat(removeCaracterPhones);
      } else {
        whatsAppNumberFormatted = "55".concat(data.whatsappNumber) || "";
      }
    }

    try {
      const updateData = {
        dealerId: dealerData.id,
        tradeName: dealerData.tradeName,
        telephone: {
          areaCode: dealerData.telephone?.areaCode || "",
          countryCode: dealerData.telephone?.countryCode || "",
          number: dealerData.telephone?.number || "",
        },
        cellphone: {
          areaCode: dealerData.cellphone?.areaCode || "",
          countryCode: dealerData.cellphone?.countryCode || "",
          number: dealerData.cellphone?.number || "",
        },
        getnet: {
          sellerId: "",
          clientId: "",
          clientSecret: "",
        },
        whatsAppNumber: whatsAppNumberFormatted,
        unit: dealerData.unit,
        subunit: dealerData.subunit,
      };

      await dispatch(handleUpdateDealerCareDetailsRequest(updateData)).unwrap();

      toast("WhatsApp atualizado com sucesso!", { type: "success" });
      setIsEditing(false);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Falha ao atualizar revenda, tente novamente!",
      );
      toast(errorMessage, { type: "error" });
    }
  };

  return (
    <Styled.Container>
      <Styled.Header>
        <Styled.HeaderTitle variant="caption">
          {t("whatsappOrders")}
        </Styled.HeaderTitle>
        {dealerData?.whatsAppNumber ? (
          <Styled.ChipActive label={t("whatsappActive")} size="small" />
        ) : (
          <Styled.ChipInactive label={t("whatsappInactive")} size="small" />
        )}
      </Styled.Header>

      <Styled.FormContainer>
        <Styled.FieldContainer>
          <Controller
            name="whatsappNumber"
            control={control}
            rules={{
              required: t("requiredField"),
              validate: (value) => {
                const cleanedValue = removeCaracterPhone(value || "");
                if (!cleanedValue || cleanedValue.length === 0) {
                  return t("requiredField");
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <InputMask
                mask="(99) 99999-9999"
                value={field.value || ""}
                onChange={field.onChange}
                disabled={!isEditing}
              >
                {(inputProps) => {
                  const { color, size, ...textFieldProps } =
                    inputProps as React.InputHTMLAttributes<HTMLInputElement>;
                  return (
                    <Styled.StyledTextField
                      {...textFieldProps}
                      fullWidth
                      label={t("responsibleNumber")}
                      variant="outlined"
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message || t("whatsappHelperText")
                      }
                    />
                  );
                }}
              </InputMask>
            )}
          />
        </Styled.FieldContainer>

        <Styled.FieldContainer>
          <Styled.StyledTextField
            fullWidth
            label={t("resaleNameInApp")}
            value={dealerData?.tradeName || ""}
            disabled
            variant="outlined"
            helperText={t("resaleNameHelperText")}
            InputLabelProps={{ shrink: true }}
          />
        </Styled.FieldContainer>
      </Styled.FormContainer>

      <Divider />

      <Styled.ActionsContainer>
        {!isEditing ? (
          <Styled.PrimaryButton variant="contained" onClick={handleEditClick}>
            {t("edit")}
          </Styled.PrimaryButton>
        ) : (
          <>
            <Styled.CancelButton variant="outlined" onClick={handleCancelEdit}>
              Cancelar
            </Styled.CancelButton>
            <Styled.PrimaryButton
              variant="contained"
              onClick={handleSubmit(onSubmit)}
            >
              Aplicar
            </Styled.PrimaryButton>
          </>
        )}
      </Styled.ActionsContainer>
    </Styled.Container>
  );
};

export default DealerWhatsAppConfig;
